import { observe } from 'mobx';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router-dom';
import styled from 'styled-components';

import {
  AppStore,
  CarStatusStore,
  CarStore,
  CurrentUserStore,
  ReportsDataStore,
  ShopStore,
  UserStore,
} from 'stores';
import { UserRoles } from 'stores/Classes/UserObject';
import HeaderMessage from '../Report/HeaderMessage';

import { Card, Col, notification, Row, Typography } from 'antd';
import { DealershipChooser } from 'components';
import _ from 'lodash';

import { DoughnutChart } from 'shared/PitstopUI/PitstopChart';

import AssetTypeChooser from 'components/Vehicles/AssetTypeChooser';
import VehiclesTable from './VehiclesTable';

import VehicleAvailabilityCard from 'components/Vehicles/VehicleAvailabilityCard';
import DVIRStatWidget from 'components/Widgets/DVIRStatWidget';
import PMCountWidget from 'components/Widgets/PMCountWidget';
import SafetyInspectionsCountWidget from 'components/Widgets/SafetyInspectionsCountWidget';
import AssetsWithIssuesWidget from 'components/Widgets/AssetsWithIssuesWidget';
import AssetsWithEngineLightsWidget from 'components/Widgets/AssetsWithEngineLightsWidget';
import ErrorBoundary from 'shared/ErrorBoundary';
import { Logger } from 'stores/Classes';
import DeviceStore from '../../stores/Classes/DeviceStore';
import * as Sentry from '@sentry/react';

const Wrapper = styled.div`
  position: relative;

  .summary-card {
    width: 100%;
    height: 357px;

    .ant-card-body {
      height: 100%;
    }

    .summary-card__title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;

      button {
        span {
          color: #ececec;
        }
      }

      span {
        color: #1f3eb8;
      }

      .filter-helper {
        font-size: 0.8rem;
        font-style: italic;
        font-weight: normal;
      }
    }

    .list {
      padding-top: 2rem;
      max-height: 250px;
      overflow-y: auto;

      .item {
        font-size: 1rem;

        .count {
          font-size: 1.5rem;
          margin-right: 1rem;
        }

        .ant-progress-text {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        &.progress-item-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          flex-wrap: wrap;

          .progress-item-wrapper__item {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;

            &:hover {
              cursor: pointer;

              span {
                text-decoration: underline;
                text-decoration-color: #9b9b9b;
                text-decoration-thickness: 1px;
                text-underline-offset: 3px;
              }
            }

            .progress-item-wrapper__item__title {
              font-size: 1.2rem;
              text-align: center;
            }
          }
        }
      }
    }

    .vehicleAvailability {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
    }
  }
`;

const { Title, Text } = Typography;

const CarsPage = (props) => {
  const { history } = props;
  const assetsTableRef = useRef(null);
  const isMountedRef = useRef(false);

  // Get currentType from URL query params
  const queryParams = new URLSearchParams(history.location.search);
  const initialCurrentType = queryParams.get('currentType') || 'vehicles';

  const [state, setState] = useState({
    isLoadingIssuesAndInsights: true,
    assetsWithIssuesCount: {
      total: 0,
      critical: 0,
      major: 0,
      minor: 0,
    },
    isLoadingVehicleAvailability: false,
    isLoadingPMStatuses: false,
    isLoadingDvirStatuses: false,
    isLoadingPMCounts: false,
    isLoadingSafetyInspectionCounts: false,
    isLoadingDvir: false,
    currentShopStatuses: [],
    vehicleAvailability: [],
    dvirStatuses: [],
    dvirValues: {},
    pmCounts: {
      overdue_count: '0',
      due_soon_count: '0',
    },
    faultCodesAndInsightsData: {
      key: '',
      seriesName: 'issues',
      emptyMessage: 'Your vehicle has no Issues',
      value: [
        {
          name: 'critical',
          color: '#eb3d34',
          y: 1,
        },
        {
          name: 'major',
          color: '#e28931',
          y: 2,
        },
        {
          name: 'minor',
          color: '#e2eb34',
          y: 3,
        },
      ],
    },
    runningData: {
      key: '',
      seriesName: 'issues',
      emptyMessage: 'Your vehicle has no Issues',
      value: [
        {
          name: 'critical',
          color: '#eb3d34',
          y: 1,
        },
        {
          name: 'major',
          color: '#e28931',
          y: 2,
        },
        {
          name: 'minor',
          color: '#e2eb34',
          y: 3,
        },
      ],
    },
    currentType: initialCurrentType,
    loadingAssetsCount: false,
    assetsCountSummary: {},
    assetsEngineLightsData: {
      stopLamp: 0,
      protectLamp: 0,
      milLamp: 0,
      checkEngine: 0,
    },
  });

  const updateState = (newState) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const loadTotalAssetsCount = async () => {
    try {
      updateState({ loadingAssetsCount: true });
      const assetsCountSummary = await CarStore.getShopAssetsCountSummary(
        ShopStore.currentShop.id
      );
      if (isMountedRef.current) {
        updateState({
          assetsCountSummary: _.first(assetsCountSummary) || {},
        });
      }
    } catch (error) {
      Logger.error(error);
      AppStore.addError(error.message || 'Failed to load assets count');
    } finally {
      if (isMountedRef.current) {
        updateState({ loadingAssetsCount: false });
      }
    }
  };

  const loadPMCounts = async () => {
    const shopId = ShopStore.currentShop.id;
    const { currentType } = state;
    try {
      updateState({ isLoadingPMCounts: true });
      const pmCounts =
        currentType === 'vehicles'
          ? await CarStore.getShopPMStatusCounts(shopId)
          : await CarStore.getShopTrailersPMStatusCounts(shopId);

      updateState({ pmCounts: pmCounts.data || {} });
    } catch (error) {
      Logger.error(error);
    } finally {
      updateState({ isLoadingPMCounts: false });
    }
  };

  const formattedAssetsWithIssues = (summaryData = []) => {
    let critical = 0,
      major = 0,
      minor = 0;

    _.forEach(summaryData, (item) => {
      if (item.priority === 0 || item.priority === 1) {
        minor += item.asset_count;
      }

      if (item.priority === 2 || item.priority === 3) {
        major += item.asset_count;
      }

      if (item.priority === 4 || item.priority === 5) {
        critical += item.asset_count;
      }
    });

    return {
      total: _.sum([critical, major, minor]),
      critical: critical,
      major: major,
      minor: minor,
    };
  };

  const loadAssetsIssuesSummary = async () => {
    try {
      updateState({ isLoadingIssuesAndInsights: true });
      const response = await ReportsDataStore.getAssetsIssuesSummary(
        ShopStore.currentShop.id
      );

      const issuesData = response?.assetsWithIssues || [];
      const engineLightsData = response?.assetsWithEngineLights || {};

      // Format engine lights data to match component structure
      const formattedEngineLightsData = {
        stopLamp: engineLightsData.redStopLamp || 0,
        protectLamp: engineLightsData.protectWarningLamp || 0,
        milLamp: engineLightsData.malfunctionLamp || 0,
        checkEngine: engineLightsData.amberWarningLamp || 0,
      };

      updateState({
        assetsWithIssuesCount: formattedAssetsWithIssues(issuesData),
        assetsEngineLightsData: formattedEngineLightsData,
      });
    } catch (error) {
      Logger.error(error);
      notification.error({
        message: 'Error',
        description: 'Unable to load assets with issues data',
      });
    } finally {
      updateState({ isLoadingIssuesAndInsights: false });
    }
  };

  const loadDviRSummary = async () => {
    const { currentType } = state;

    updateState({ isLoadingDvir: true });
    try {
      const summaryData =
        currentType === 'vehicles'
          ? await ReportsDataStore.getShopVehiclesDvirStatusSummary(
              ShopStore.currentShop.id
            )
          : await ReportsDataStore.getShopTrailersDvirStatusSummary(
              ShopStore.currentShop.id
            );
      updateState({
        dvirValues: summaryData,
      });
    } catch (error) {
      Logger.error(error);
      // if AbortError, do not show notification
      if (error.name === 'AbortError') {
        return;
      }
      Sentry.captureException(error, {
        tags: {
          component: 'VehiclesPage',
          function: 'loadDviRSummary',
          currentType: state.currentType,
        },
        extra: {
          shopId: ShopStore.currentShop.id,
          errorMessage: error.message,
        },
      });
    } finally {
      updateState({ isLoadingDvir: false });
    }
  };

  const loadDvirStatuses = async () => {
    try {
      updateState({ isLoadingDvirStatuses: true });
      const dvirStatuses = await ReportsDataStore.getDvirStatusSummary();
      updateState({ dvirStatuses: dvirStatuses });
    } catch (error) {
      Logger.error(error);
    } finally {
      updateState({ isLoadingDvirStatuses: false });
    }
  };

  const loadShopUsers = async () => {
    const shopUsers = await UserStore.fetchShopUsers(ShopStore.currentShop.id);
    ShopStore.setShopUsers(shopUsers);
  };

  const clearQueryParams = () => {
    const queryParams = new URLSearchParams(history.location.search);
    queryParams.delete('status');
    queryParams.delete('dvir');
    history.push({
      search: queryParams.toString(),
    });
  };

  const loadVehicleAvailabilityAndShopStatuses = async () => {
    const { currentType } = state;
    const shopId = ShopStore.currentShop.id;

    const currentTypeKey = {
      vehicles: 'vehicle',
      trailers: 'trailer',
    };

    updateState({ isLoadingVehicleAvailability: true });

    try {
      const vehicleAvailability =
        await CarStore.getShopVehicleAvailabilitySummary(
          ShopStore.currentShop.id,
          currentTypeKey[currentType]
        );
      const shopStatuses = await CarStatusStore.fetchShopCarStatuses(shopId);
      const availabilityKeys = Object.keys(vehicleAvailability);
      const validTotal = availabilityKeys.reduce((acc, key) => {
        return acc + vehicleAvailability[key];
      }, 0);
      let formattedVehicleAvailability = availabilityKeys
        .map((key) => {
          const value = vehicleAvailability[key];
          let percent = (value / validTotal) * 100;
          let shopStatus = shopStatuses
            ? shopStatuses.find((status) => status.key === key)
            : null;
          if (!shopStatus) {
            // get the default status
            shopStatus = shopStatuses
              ? shopStatuses.find((status) => status.is_default)
              : null;
          }
          return {
            title: shopStatus?.description || 'Running',
            value: percent,
            color: shopStatus?.color || 'green',
            count: value,
          };
        })
        .sort((a, b) => b.value - a.value);
      // combine duplicated descriptions
      formattedVehicleAvailability = formattedVehicleAvailability.reduce(
        (acc, item) => {
          const existingItem = acc.find((i) => i.title === item.title);
          if (existingItem) {
            existingItem.value += item.value;
            existingItem.count += item.count;
            return acc;
          }
          acc.push(item);
          return acc;
        },
        []
      );

      loadPMCounts();
      updateState({
        vehicleAvailability: formattedVehicleAvailability,
      });
    } catch (error) {
      console.error(error);
    } finally {
      updateState({ isLoadingVehicleAvailability: false });
    }
  };

  const addFiltersToQuery = (type = '', value) => {
    if (!type) {
      throw new Error('Type is required');
    }
    // push the filter to the URL
    // get the current query params
    const queryParams = new URLSearchParams(history.location.search);

    // set the type query param
    queryParams.set(type, value);

    // push the new query params to the URL
    history.push({
      search: queryParams.toString(),
    });
  };

  const handlePMsCountClick = (sectionKey) => {
    if (assetsTableRef.current) {
      assetsTableRef.current.handlePmsCountSectionClick(sectionKey);
    }
  };

  const handleSafetyInspectionsCountClick = (sectionKey) => {
    if (assetsTableRef.current) {
      assetsTableRef.current.handleSafetyInspectionsCountSectionClick(
        sectionKey
      );
    }
  };

  const handleIssuesCountClick = (sectionKey) => {
    if (assetsTableRef.current) {
      assetsTableRef.current.handleIssuesCountSectionClick(sectionKey);
    }
  };

  const handleEngineLightsClick = (lightType) => {
    if (assetsTableRef.current) {
      assetsTableRef.current.handleEngineLightsCountSectionClick(lightType);
    }
  };

  // Component lifecycle equivalents
  useEffect(() => {
    AppStore.setSelectedSidebarKey('/vehicles');
    isMountedRef.current = true;

    if (CurrentUserStore.user.role !== UserRoles.customer) {
      if (!ShopStore.loaded && !ShopStore.pending) {
        ShopStore.getShops();
      }
    }

    // check query params to see if currentType is set
    // and if it is, set the value to the currentType state
    const queryParams = new URLSearchParams(history.location.search);
    const currentType = queryParams.get('currentType');
    if (currentType) {
      if (assetsTableRef.current) {
        assetsTableRef.current.handleSearchData();
      }
      updateState({ currentType });
    }

    loadTotalAssetsCount();
    loadVehicleAvailabilityAndShopStatuses();
    loadPMCounts();
    loadDvirStatuses();
    loadAssetsIssuesSummary();
    loadDviRSummary();
    loadShopUsers();

    // clear the query params
    clearQueryParams();

    // Set up MobX observer
    const disposer = observe(ShopStore.currentShop, 'id', async () => {
      if (assetsTableRef.current) {
        assetsTableRef.current.loadData(state.currentType);
      }
      loadTotalAssetsCount();
      loadVehicleAvailabilityAndShopStatuses();
      loadPMCounts();
      loadDvirStatuses();
      loadAssetsIssuesSummary();
      clearQueryParams();
      loadDviRSummary();
      loadShopUsers();
    });

    // Cleanup
    return () => {
      isMountedRef.current = false;
      disposer();
    };
  }, []);

  // componentDidUpdate equivalent for currentType changes
  useEffect(() => {
    loadVehicleAvailabilityAndShopStatuses();
    loadDviRSummary();
    loadAssetsIssuesSummary();
  }, [state.currentType]);

  return (
    <Wrapper>
      <Row>
        <Col span={24}>
          <Title>Asset Management</Title>
        </Col>
      </Row>
      <Row>
        <Col span={['mobile', 'tablet'].includes(DeviceStore.device) ? 24 : 16}>
          <DealershipChooser />
          <HeaderMessage />
        </Col>
      </Row>
      <Row
        style={{
          marginTop: '1rem',
        }}
      >
        <Col span={6}>
          <AssetTypeChooser
            currentType={state.currentType}
            onChange={(type) => {
              updateState({ currentType: type });
              // set the current type to query param
              addFiltersToQuery('currentType', type);
              if (assetsTableRef.current) {
                assetsTableRef.current.loadData(type);
              }
            }}
            loading={state.loadingAssetsCount}
            vehiclesCount={state.assetsCountSummary?.countVehicles}
            trailersCount={state.assetsCountSummary?.countTrailers}
          />
        </Col>
      </Row>
      {['desktop', 'tablet'].includes(DeviceStore.device) && (
        <Row gutter={[16, 16]}>
          <Col span={8}>
            <VehicleAvailabilityCard
              isLoadingVehicleAvailability={state.isLoadingVehicleAvailability}
              vehicleAvailability={state.vehicleAvailability}
              addFiltersToQuery={addFiltersToQuery}
            />
          </Col>

          {state.currentType === 'vehicles' && (
            <Col
              span={6}
              style={{ display: 'flex', gap: 16, flexDirection: 'column' }}
            >
              <AssetsWithIssuesWidget
                isLoading={state.isLoadingIssuesAndInsights}
                issuesData={{
                  critical: _.get(state.assetsWithIssuesCount, 'critical', 0),
                  major: _.get(state.assetsWithIssuesCount, 'major', 0),
                  minor: _.get(state.assetsWithIssuesCount, 'minor', 0),
                }}
                onIssueClick={handleIssuesCountClick}
              />
              <AssetsWithEngineLightsWidget
                isLoading={state.isLoadingIssuesAndInsights}
                assetsEngineLightsData={state.assetsEngineLightsData}
                onEngineLightClick={handleEngineLightsClick}
              />
            </Col>
          )}
          <Col
            span={state.currentType === 'vehicles' ? 4 : 6}
            style={{ display: 'flex', gap: 16, flexDirection: 'column' }}
          >
            <PMCountWidget
              isLoadingPMCounts={state.isLoadingPMCounts}
              pmCounts={state.pmCounts}
              handlePMsCountClick={handlePMsCountClick}
            />
            <SafetyInspectionsCountWidget
              isLoading={state.isLoadingPMCounts}
              counts={state.pmCounts}
              handleClick={handleSafetyInspectionsCountClick}
            />
          </Col>
          <Col span={6}>
            <DVIRStatWidget
              isTrailer={state.currentType === 'trailers'}
              shopId={ShopStore.currentShop.id}
            />
          </Col>
        </Row>
      )}

      <Row gutter={[16, 16]}>
        <Col span={24}>
          <ErrorBoundary>
            <VehiclesTable
              loadTotalAssetsCount={loadTotalAssetsCount}
              childRef={(ref) => {
                assetsTableRef.current = ref;
              }}
              type={state.currentType}
              onUpdateCarStatus={() => {
                loadVehicleAvailabilityAndShopStatuses();
              }}
            />
          </ErrorBoundary>
        </Col>
      </Row>
    </Wrapper>
  );
};

CarsPage.propTypes = {
  history: PropTypes.object,
};

export default withRouter(observer(CarsPage));
